import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowRight, ArrowLeft, Check, CalendarClock } from "lucide-react";
import { Reveal, MaskedLines, Eyebrow, EASE } from "@/components/motion";
import { api, formatApiError } from "@/lib/api";

const SERVICES = ["Credit Repair Audit", "Funding Consultation", "Business Credit", "General Question"];
const TIMES = ["9:00 AM", "10:30 AM", "12:00 PM", "2:00 PM", "3:30 PM", "5:00 PM"];

const normalizePhone = (p) => {
  const digits = (p || "").replace(/\D/g, "");
  if ((p || "").trim().startsWith("+")) return p.trim();
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return (p || "").trim();
};

function VerifyPanel({ email, phone, inputCls, onVerified }) {
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const normalized = normalizePhone(phone);

  useEffect(() => {
    api.get("/verify/config").then((r) => setSmsEnabled(r.data.sms_enabled)).catch(() => {});
  }, []);

  const sendCodes = async () => {
    setBusy(true);
    try {
      await api.post("/verify/email/send", { email });
      if (smsEnabled) await api.post("/verify/phone/send", { phone: normalized });
      setSent(true);
      toast.success(smsEnabled ? "Codes sent to your email and phone." : "A code was sent to your email.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const er = await api.post("/verify/email/confirm", { contact: email, code: emailCode });
      let phoneToken = null;
      if (smsEnabled) {
        const pr = await api.post("/verify/phone/confirm", { contact: normalized, code: phoneCode });
        phoneToken = pr.data.token;
      }
      setDone(true);
      onVerified({ emailToken: er.data.token, phoneToken, phone: normalized });
      toast.success("Verified. You're all set.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex items-center gap-3 bg-emerald/10 border border-emerald/30 rounded-xl px-5 py-4" data-testid="verify-success">
        <Check className="w-5 h-5 text-emerald" />
        <p className="text-sm text-emerald">{smsEnabled ? "Email and phone verified." : "Email verified."}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="verify-panel">
      <p className="text-slate-400 text-sm leading-relaxed">
        One quick step so we know it's really you: we'll send a 6-digit code to{" "}
        <span className="text-white">{email}</span>
        {smsEnabled && <> and a text to <span className="text-white">{normalized}</span></>}.
      </p>
      {!sent ? (
        <button type="button" onClick={sendCodes} disabled={busy} data-testid="verify-send-button"
          className="bg-white/5 border border-white/15 text-white hover:bg-white/10 hover:border-gold/40 font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 rounded-xl transition-all disabled:opacity-50">
          {busy ? "Sending…" : `Send Verification Code${smsEnabled ? "s" : ""}`}
        </button>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input placeholder="Email code" value={emailCode} data-testid="verify-email-code-input"
              onChange={(e) => setEmailCode(e.target.value)} className={inputCls} maxLength={6} />
            {smsEnabled && (
              <input placeholder="Text message code" value={phoneCode} data-testid="verify-phone-code-input"
                onChange={(e) => setPhoneCode(e.target.value)} className={inputCls} maxLength={6} />
            )}
          </div>
          <button type="button" onClick={confirm} disabled={busy || !emailCode || (smsEnabled && !phoneCode)}
            data-testid="verify-confirm-button"
            className="bg-white/5 border border-white/15 text-white hover:bg-white/10 hover:border-gold/40 font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 rounded-xl transition-all disabled:opacity-40">
            {busy ? "Verifying…" : "Verify"}
          </button>
        </>
      )}
    </div>
  );
}

function MessageCard({ inputCls }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [tokens, setTokens] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const fieldsReady = form.name && form.email && form.phone && form.message;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name, email: form.email, message: form.message,
        phone: tokens?.phone || form.phone,
        email_token: tokens?.emailToken, phone_token: tokens?.phoneToken,
      });
      setSent(true);
      toast.success("Message sent. We'll reply within one business day.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#101218] border border-white/10 rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.37)]" data-testid="contact-message-card">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-2">Quick Message</p>
      {sent ? (
        <p className="text-slate-300 text-sm flex items-center gap-2 mt-4" data-testid="message-sent">
          <Check className="w-4 h-4 text-emerald" /> Message received. We'll be in touch shortly.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-3 mt-4">
          <input required placeholder="Name" value={form.name} data-testid="message-name-input"
            onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          <input required type="email" placeholder="Email" value={form.email} data-testid="message-email-input"
            onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
          <input required placeholder="Phone" value={form.phone} data-testid="message-phone-input"
            onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
          <textarea required placeholder="How can we help?" rows={3} value={form.message}
            data-testid="message-body-input" onChange={(e) => setForm({ ...form, message: e.target.value })}
            className={`${inputCls} resize-none`} />
          {fieldsReady && !tokens && (
            <VerifyPanel email={form.email} phone={form.phone} inputCls={inputCls} onVerified={setTokens} />
          )}
          <button type="submit" disabled={sending || !tokens} data-testid="message-send-button"
            className="w-full bg-white/5 border border-white/15 text-white hover:bg-white/10 hover:border-gold/40 font-mono text-xs uppercase tracking-[0.15em] px-5 py-3.5 rounded-xl transition-all disabled:opacity-50">
            {sending ? "Sending…" : tokens ? "Send Message" : "Verify Email to Send"}
          </button>
        </form>
      )}
    </div>
  );
}

export default function Contact() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ service: "", date: "", time: "", name: "", email: "", phone: "", notes: "" });
  const [tokens, setTokens] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canNext = step === 0 ? !!form.service : step === 1 ? !!form.date && !!form.time : !!form.name && !!form.email && !!form.phone;

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        name: form.name, email: form.email, phone: tokens?.phone || form.phone,
        service: form.service, date: form.date, time: form.time, notes: form.notes,
        email_token: tokens?.emailToken, phone_token: tokens?.phoneToken,
      });
      setDone(true);
      toast.success("Call booked. Check your inbox for confirmation details.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-[#171A22] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all text-sm";

  return (
    <div data-testid="contact-page" className="py-28 md:py-36 px-6 lg:px-12 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(93,123,255,0.05),transparent_55%)]" />
      <div className="max-w-7xl mx-auto relative">
        <div className="mb-16 max-w-2xl">
          <Eyebrow className="mb-6">Book-A-Call</Eyebrow>
          <MaskedLines
            lines={["Speak with a senior", "credit architect."]}
            className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium tracking-tight leading-[1.08] text-white"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
            className="mt-6 text-slate-400"
          >
            Fifteen minutes. Zero obligation. Leave with a map of exactly what's holding your score down.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Wizard */}
          <div className="lg:col-span-2">
            <Reveal>
              <div data-testid="booking-wizard" className="bg-[#101218] border border-white/10 rounded-2xl p-8 md:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.37)]">
                {done ? (
                  <div data-testid="booking-confirmation" className="text-center py-10">
                    <div className="w-16 h-16 rounded-full bg-emerald/10 border border-emerald/30 flex items-center justify-center mx-auto mb-6">
                      <Check className="w-8 h-8 text-emerald" />
                    </div>
                    <h3 className="font-serif text-2xl text-white mb-3">You're booked, {form.name.split(" ")[0]}.</h3>
                    <p className="text-slate-400 text-sm leading-relaxed max-w-md mx-auto">
                      {form.service} · {form.date} at {form.time}. Check {form.email} for your confirmation email
                      with your Google Meet link. Come prepared with any recent credit reports if you have them.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Step indicator */}
                    <div className="flex items-center gap-3 mb-10" data-testid="wizard-steps">
                      {["Service", "Schedule", "Details", "Verify"].map((s, i) => (
                        <div key={s} className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs border transition-colors ${
                            i < step ? "bg-emerald/20 border-emerald/40 text-emerald" : i === step ? "bg-gold/15 border-gold/50 text-gold" : "border-white/15 text-slate-500"
                          }`}>
                            {i < step ? <Check className="w-4 h-4" /> : i + 1}
                          </div>
                          <span className={`font-mono text-[10px] uppercase tracking-widest hidden sm:block ${i === step ? "text-white" : "text-slate-500"}`}>{s}</span>
                          {i < 3 && <div className="w-8 h-px bg-white/10" />}
                        </div>
                      ))}
                    </div>

                    <AnimatePresence mode="wait">
                      <motion.div
                        key={step}
                        initial={{ opacity: 0, x: 24 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -24 }}
                        transition={{ duration: 0.35, ease: EASE }}
                      >
                        {step === 0 && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="wizard-step-service">
                            {SERVICES.map((s) => (
                              <button key={s} type="button" data-testid={`service-option-${s.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => set("service", s)}
                                className={`text-left p-6 rounded-xl border transition-all duration-200 ${
                                  form.service === s ? "border-gold bg-gold/10 text-white" : "border-white/10 bg-elevated text-slate-300 hover:border-white/25"
                                }`}>
                                <span className="font-serif text-lg">{s}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        {step === 1 && (
                          <div className="space-y-6" data-testid="wizard-step-schedule">
                            <div>
                              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500 block mb-3">Preferred Date</label>
                              <input type="date" value={form.date} min={new Date().toISOString().split("T")[0]}
                                data-testid="booking-date-input" onChange={(e) => set("date", e.target.value)}
                                className={`${inputCls} [color-scheme:dark]`} />
                            </div>
                            <div>
                              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500 block mb-3">Time (ET)</label>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {TIMES.map((t) => (
                                  <button key={t} type="button" data-testid={`time-slot-${t.replace(/[:\s]/g, "-").toLowerCase()}`}
                                    onClick={() => set("time", t)}
                                    className={`py-3 rounded-xl border font-mono text-xs transition-all duration-200 ${
                                      form.time === t ? "border-gold bg-gold/10 text-gold" : "border-white/10 bg-elevated text-slate-300 hover:border-white/25"
                                    }`}>
                                    {t}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        {step === 2 && (
                          <div className="space-y-4" data-testid="wizard-step-details">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <input required placeholder="Full name" value={form.name} data-testid="booking-name-input" onChange={(e) => set("name", e.target.value)} className={inputCls} />
                              <input required placeholder="Phone" value={form.phone} data-testid="booking-phone-input" onChange={(e) => set("phone", e.target.value)} className={inputCls} />
                            </div>
                            <input required type="email" placeholder="Email" value={form.email} data-testid="booking-email-input" onChange={(e) => set("email", e.target.value)} className={inputCls} />
                            <textarea placeholder="Anything we should know? (optional)" value={form.notes} rows={3}
                              data-testid="booking-notes-input" onChange={(e) => set("notes", e.target.value)}
                              className={`${inputCls} resize-none`} />
                          </div>
                        )}
                        {step === 3 && (
                          <div data-testid="wizard-step-verify">
                            <VerifyPanel email={form.email} phone={form.phone} inputCls={inputCls} onVerified={setTokens} />
                          </div>
                        )}
                      </motion.div>
                    </AnimatePresence>

                    <div className="flex justify-between mt-10">
                      <button type="button" onClick={() => setStep(step - 1)} disabled={step === 0}
                        data-testid="wizard-back-button"
                        className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 rounded-xl bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed">
                        <ArrowLeft className="w-4 h-4" /> Back
                      </button>
                      {step < 3 ? (
                        <button type="button" onClick={() => canNext && setStep(step + 1)} disabled={!canNext}
                          data-testid="wizard-next-button"
                          className="flex items-center gap-2 bg-gold text-obsidian hover:bg-gold-light font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                          Next <ArrowRight className="w-4 h-4" />
                        </button>
                      ) : (
                        <button type="button" onClick={submit} disabled={submitting || !tokens}
                          data-testid="book-call-submit-button"
                          className="flex items-center gap-2 bg-gold text-obsidian hover:bg-gold-light hover:scale-[1.02] active:scale-[0.98] font-mono text-xs uppercase tracking-[0.15em] px-8 py-3.5 rounded-xl transition-all shadow-[0_0_20px_rgba(93,123,255,0.25)] disabled:opacity-40">
                          {submitting ? "Booking…" : tokens ? "Confirm Booking" : "Verify Email First"} <CalendarClock className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </Reveal>
          </div>

          {/* Direct contact */}
          <Reveal delay={0.15} className="space-y-6">
            <div className="bg-[#101218] border border-white/10 rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.37)]">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-6">Direct Lines</p>
              <div className="space-y-5 text-sm">
                <a href="tel:7278557612" data-testid="contact-phone" className="flex items-center gap-4 text-slate-300 hover:text-white transition-colors group">
                  <span className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center group-hover:bg-gold/20 transition-colors"><Phone className="w-4 h-4 text-gold" /></span>
                  (727) 855-7612
                </a>
                <a href="mailto:clearpathrepair.enterprise@gmail.com" data-testid="contact-email" className="flex items-center gap-4 text-slate-300 hover:text-white transition-colors group">
                  <span className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center group-hover:bg-gold/20 transition-colors"><Mail className="w-4 h-4 text-gold" /></span>
                  clearpathrepair.enterprise@gmail.com
                </a>
              </div>
            </div>
            <div className="bg-[#101218] border border-white/10 rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.37)]">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-6">Headquarters</p>
              <div className="space-y-5 text-sm text-slate-400">
                <div className="flex gap-4">
                  <MapPin className="w-4 h-4 text-gold mt-1 shrink-0" />
                  <div><p className="text-white">Tampa, Florida</p><p>Headquarters</p></div>
                </div>
                <div className="flex gap-4">
                  <MapPin className="w-4 h-4 text-gold mt-1 shrink-0" />
                  <div><p className="text-white">Washington, DC</p><p>Headquarters</p></div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-elevated to-surface border border-gold/25 rounded-2xl p-8">
              <p className="font-serif text-lg text-white mb-2">Prefer to talk it through?</p>
              <p className="text-slate-400 text-sm mb-5">Call us directly and we'll map your options on the spot.</p>
              <a href="tel:7278557612" data-testid="contact-call-link" className="font-mono text-xs uppercase tracking-[0.15em] text-gold hover:text-gold-light flex items-center gap-2 transition-colors">
                Call (727) 855-7612 <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <MessageCard inputCls={inputCls} />
          </Reveal>
        </div>
      </div>
    </div>
  );
}
